import { Box, Typography, type BoxProps } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

export interface ProfileStat {
  label: string;
  value: number;
  max?: number;
}

const fillIn = keyframes`
  from { opacity: 0; transform: scaleX(0.85); }
  to   { opacity: 1; transform: scaleX(1); }
`;

const StatRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const StatHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
});

const BarRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 2,
  fontFamily: theme.typography.fontFamily,
  lineHeight: 1,
}));

const Block = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  width: 10,
  height: 14,
  backgroundColor: active ? theme.palette.primary.main : theme.palette.divider,
  opacity: active ? 0.9 : 0.4,

  boxShadow: active ? '0 0 6px rgba(125,211,199,0.4)' : 'none',

  animation: active ? `${fillIn} 300ms ease-out` : 'none',
}));

export function StatBar({
  label,
  value,
  max = 10,
  ...props
}: ProfileStat & BoxProps) {
  const clamped = Math.max(0, Math.min(value, max));

  return (
    <StatRoot {...props}>
      <StatHeader>
        <Typography
          variant="caption"
          sx={{
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          {label}
        </Typography>

        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {clamped} / {max}
        </Typography>
      </StatHeader>

      <BarRow>
        {Array.from({ length: max }).map((_, i) => (
          <Block key={i} active={i < clamped} />
        ))}
      </BarRow>
    </StatRoot>
  );
}
